import type { NovelItem } from '@app/_proto/Protos/novels';
import { GetSponsorPlansRequest_SortDirection, GetSponsorPlansRequest_SortType } from '@app/_proto/Protos/sponsors';
import QueryProgress from '@app/components/query.progress';
import { CHAMPION_EVENT_SOURCES } from '@app/domains/champion/analytics/posthog/constants';
import ChampionPurchaseDialog from '@app/domains/champion/components/ChampionPurchaseDialog';
import { UNLIMITED_ADVANCE_CHAPTERS_COUNT } from '@app/domains/champion/constants';
import useChampionPlans from '@app/domains/champion/hooks/useChampionPlans';
import useChampionSubscriptions from '@app/domains/champion/hooks/useChampionSubscriptions';
import { convertMinusException } from '@app/domains/champion/utils/convertMinusException';
import AdvancedChapterLockView from '@app/domains/chapter/components/AdvancedChapterLockView';
import useMediaQueries from '@app/domains/common/hooks/useMediaQueries';
import { useAuth } from '@app/libs/auth';
import { getOneofValue } from '@app/libs/grpc';
import { max } from 'lodash-es';
import { useLayoutEffect, useMemo, useState } from 'react';

type Props = {
    novel: NovelItem;
    onSubscribed: () => void;
};

export default function AdvancedChapterLock({ novel, onSubscribed }: Props) {
    const { user, login } = useAuth();
    const { isDownSm } = useMediaQueries();
    const [tierShowCountLimit, setTierShowCountLimit] = useState(3);
    const [isShowingAllTiers, setIsShowingAllTiers] = useState(false);

    const { activeSubscription: activeChampionSubscription, isLoading: isChampionSubscriptionLoading } =
        useChampionSubscriptions({
            novelId: novel.id,
        });

    const { data: championPlans, isLoading: isChampionPlansLoading } = useChampionPlans({
        novelId: novel.id,
        sortType: GetSponsorPlansRequest_SortType.AdvanceChapters,
        sortDirection: GetSponsorPlansRequest_SortDirection.ASC,
        showModalOnError: false,
    });

    const advanceChapterCount = useMemo(() => {
        return max(
            novel.sponsorInfo?.plans.map(plan =>
                plan.advanceChapterCount === UNLIMITED_ADVANCE_CHAPTERS_COUNT
                    ? novel.sponsorInfo?.advanceChapterCount?.value
                    : plan.advanceChapterCount
            )
        );
    }, [novel.sponsorInfo]);

    const handleClickSeeMoreTiers = () => {
        if (!user) {
            return login(window.location.pathname);
        } else {
            setIsShowingAllTiers(true);
        }
    };

    const handleSubscribeChampionPlan = () => {
        onSubscribed?.();
    };

    useLayoutEffect(() => {
        const tierCountLimit = (() => {
            if (isDownSm) {
                if (activeChampionSubscription) return 2;
                else return 3;
            }
            if (!isDownSm) return 4;
            return 0;
        })();
        setTierShowCountLimit(tierCountLimit);
    }, [activeChampionSubscription, isDownSm]);

    const rearrangedChampionPlans = useMemo(() => {
        if (!championPlans) return [];
        let plansArray = [...championPlans];
        plansArray.sort(
            (a, b) => convertMinusException(a.advanceChapterCount) - convertMinusException(b.advanceChapterCount)
        );
        if (activeChampionSubscription) {
            const subscribingPlan = getOneofValue(activeChampionSubscription?.plan?.plan, 'sponsor');
            if (subscribingPlan) {
                plansArray = plansArray.filter(plan => {
                    return (
                        plan.advanceChapterCount === UNLIMITED_ADVANCE_CHAPTERS_COUNT ||
                        plan.advanceChapterCount > subscribingPlan.advanceChapterCount
                    );
                });
            }
        }
        return plansArray;
    }, [championPlans, activeChampionSubscription]);

    const visibleChampionPlans = rearrangedChampionPlans.slice(0, tierShowCountLimit);

    if (isChampionSubscriptionLoading || isChampionPlansLoading) {
        return <QueryProgress status="loading" />;
    }

    return (
        <>
            <AdvancedChapterLockView
                novelId={novel.id}
                novelName={novel.name}
                championPlans={visibleChampionPlans}
                activeChampionSubscription={activeChampionSubscription}
                advanceChapterCount={advanceChapterCount ?? 0}
                onClickMore={handleClickSeeMoreTiers}
                onSubscribePlan={handleSubscribeChampionPlan}
            />
            {championPlans && championPlans.length > 0 && isShowingAllTiers && (
                <ChampionPurchaseDialog
                    on={CHAMPION_EVENT_SOURCES.AdvancePaywallLayer}
                    novelId={novel.id}
                    novelName={novel.name}
                    activeChampionSubscription={activeChampionSubscription}
                    initialView="all-plans"
                    championPlans={championPlans}
                    onSubscribePlan={handleSubscribeChampionPlan}
                    onClose={() => {
                        setIsShowingAllTiers(false);
                    }}
                />
            )}
        </>
    );
}
